import type { RegionAliases } from "@store/useDashboardStore";
import type { BssData } from "@dashboard/base/BssService";

/** Static bindings only; live values are resolved by BssService. */
export interface BrandBssBinding {
  key: keyof BssData;
  /** May be omitted when the consumer derives its fallback from other config. */
  fallback?: string;
}

export type BrandBssKeys = Record<string, BrandBssBinding>;

export const DEFAULT_BRAND_LOCALES = {
  en_US: "English",
} as const;

export const RC_PARTNER_NAME = "RingCentral";

const RC_COPYRIGHT_EXTRA = "RingCentral, Inc. All rights reserved.";
const RC_TRADEMARK_NOTICE = "All other logos, marks, designs and otherwise are the trademarks and intellectual property of RingCentral, Inc.";
const RC_MARKS_NOTICE = "All other marks, logos, designs and otherwise are the trademarks and intellectual property of RingCentral, Inc.";

/** Static copy for BT's custom footer. */
export const BT_FOOTER_CONTENT = {
  brandTrademarkText: "The BT Corporate Mark is a registered trade mark of British Telecommunications plc. " +
    "BT Cloud Work is a trade mark of British Telecommunications plc.",
} as const;

/** Static copy for Verizon's custom header. */
export const VERIZON_HEADER_CONTENT = {
  tagline: "Service Status",
  logoTrackingName: "Logo Service Status",
} as const;

/** Original Vodafone navigation, including the legacy Video destination. */
export const VODAFONE_HEADER_CONTENT = {
  logoTrackingName: "Logo Service Status",
  supportLink: {
    label: "Support",
    href: "https://support.vodafonebusiness.ringcentral.com/gb/en/",
    className: "support",
  },
  productLinks: [
    { label: "MVP", href: "https://support.vodafonebusiness.ringcentral.com/gb/en/mvp.html", className: "mvp" },
    { label: "Message", href: "https://support.vodafonebusiness.ringcentral.com/mvp/message/intro-message-ringcentral-app.html", className: "message" },
    { label: "Video", href: "https://support.vodafonebusiness.ringcentral.com/mvp/admin.html", className: "video" },
    { label: "Phone", href: "https://support.vodafonebusiness.ringcentral.com/mvp/phone.html", className: "phone" },
    { label: "Download", href: "https://support.vodafonebusiness.ringcentral.com/download.html", className: "download" },
    { label: "Network requirements", href: "https://support.vodafonebusiness.ringcentral.com/network-and-system-requirements.html", className: "requirements" },
  ],
} as const;

/** Static copy and navigation for RC's custom header. */
export const RC_HEADER_CONTENT = {
  logoTrackingName: "Logo Service Status",
  loginTrackingName: "Login Service Status",
  loginLabel: "Login",
  logoutLabel: "Logout",
  topLinks: [
    { label: "Download", href: "https://support.ringcentral.com/download.html", className: "download" },
    { label: "Community", href: "https://community.ringcentral.com/", className: "community" },
    { label: "Submit an idea", href: "https://ideas.ringcentral.com/", className: "ideas" },
    { label: "Training", href: "https://support.ringcentral.com/ringex.html", className: "training" },
    { label: "MyRingCentral", href: "https://support.ringcentral.com/", className: "support" },
  ],
  productLinks: [
    { label: "RingEX", href: "https://support.ringcentral.com/ringex.html", className: "ring-ex" },
    { label: "Message", href: "https://support.ringcentral.com/app/message/desktop-web/intro-message-ringcentral-app.html", className: "message" },
    { label: "Video", href: "https://support.ringcentral.com/video.html", className: "video" },
    { label: "Phone", href: "https://support.ringcentral.com/mvp/phone.html", className: "phone" },
    { label: "Rooms & Connector", href: "https://support.ringcentral.com/rooms.html", className: "rooms" },
    { label: "Webinar", href: "https://support.ringcentral.com/webinar.html", className: "webinar" },
    { label: "Events", href: "https://support.ringcentral.com/events.html", className: "events" },
    { label: "RingCX", href: "https://support.ringcentral.com/ringcx.html", className: "ring-cx" },
    { label: "Contact Center", href: "https://support.ringcentral.com/contact-center.html", className: "contact" },
  ],
} as const;

/** Static social navigation for RC's custom footer. */
export const RC_FOOTER_CONTENT = {
  socialLinks: [
    {
      title: "Facebook",
      href: "https://www.facebook.com/ringcentral?refsrc=https://www.ringcentral.com%2F&_rdr",
      className: "facebook",
      trackingName: "facebook",
    },
    {
      title: "X",
      href: "https://x.com/ringcentral",
      className: "twitter-x",
      trackingName: "twitter",
    },
    {
      title: "LinkedIn",
      href: "https://www.linkedin.com/company/ringcentral/",
      className: "linkedin",
      trackingName: "linkedin",
    },
  ],
} as const;

/** Static copy for RC's dashboard and its original tabs. */
export const RC_DASHBOARD_CONTENT = {
  title: "Service Status",
  tabs: [
    { value: "services", label: "Core Services" },
    { value: "history", label: "Uptime History" },
  ],
  maintenanceLink: "Maintenance notifications",
  maintenanceTitle: "Maintenance notification",
  moreMaintenance: "See more maintenance notifications",
  incidentsTitle: "Incident status updates",
  closeLabel: "close",
  previousRegion: "Previous",
  nextRegion: "Next",
  legend: [
    { className: "board-ok", label: "Good", tooltip: "No issues are being reported" },
    { className: "board-information", label: "Information", tooltip: "Information" },
    { className: "board-warn", label: "Warning", tooltip: "We are currently investigating a potential issue" },
    { className: "board-error", label: "Issue", tooltip: "Issues are being reported" },
  ],
} as const;

export const RC_UPTIME_CONTENT = {
  disclaimer: "This page shows the historical global uptime for RingCentral products. Uptime percentages are determined globally " +
    "across the RingCentral platform and may not be indicative of individual customer experience. This page may be " +
    "updated from time to time and is for informational purposes only. This page does not form any part of any terms of " +
    "service for RingCentral Products.",
  disclaimerEmphasis: "Individual customer experience may vary.",
  title: "Uptime History",
  startDate: "Start Date:",
  endDate: "End Date:",
  date: "Date:",
  status: "Status:",
  timezone: "PDT",
  noIncidents: "- No incidents have been recorded",
  services: ["Phone", "Messaging", "Video", "RingCX", "Engage Digital", "Events", "SMS Delivery Statistics"],
  legend: [
    { className: "normal", label: "Normal", description: "Normal: no incidents have been recorded" },
    { className: "yellow", label: "Minor impact", description: "Minor impact: recorded incidents have a minor impact on users" },
    { className: "red", label: "High impact", description: "High impact: recorded incidents have a high impact on users" },
  ],
} as const;

/**
 * One footer navigation item. `className` matches the Vue 2 semantic class
 * (`legal`, `privacy`, `policy`, …) so BrandFooter can keep CId/IId hooks.
 */
export interface FooterLink {
  label: string;
  href: string;
  className?: string;
}

/** Existing footer layout differences; omitted for brands with custom footers. */
export interface BrandFooterConfig {
  layout: "standard" | "att" | "copyright";
  copyClass?: "footer-copy";
  menuClass?: "footer-menu" | "flex row footer-menu" | "flex row footer-links";
  copyrightId?: "copyright";
  copyrightSuffix?: string;
  noticeLines?: string[];
  trailingBreak?: boolean;
  showRingCentralLogo?: boolean;
}

export type BrandHeaderType = "shared" | "rc" | "telus" | "rise" | "vodafone" | "verizon";
export type BrandFooterType = "shared" | "bt" | "rc" | "telus" | "vodafone";

/**
 * Shared per-brand dashboard + chrome settings (GWS-18470).
 * Optional ATT/BT BSS bindings accompany the static fallbacks (GWS-19025).
 */
export interface BrandConfig {
  brand: string;
  regionAliases: RegionAliases;
  loadBanners: boolean;
  locales: Record<string, string>;
  copyrightYearFrom: string;
  copyrightExtraText: string;
  logoId: string;
  logoHref: string;
  /** Identifies the shared or custom header, independently of migration readiness. */
  headerComponent: BrandHeaderType;
  /** Set for brands that use the shared logo-only header. */
  logoElement?: "a" | "div";
  footerLinks: FooterLink[];
  /** Identifies the shared or custom footer, independently of migration readiness. */
  footerComponent: BrandFooterType;
  footer?: BrandFooterConfig;
  bssKeys?: BrandBssKeys;
}

export type BrandConfigRecord = Omit<BrandConfig, "brand">;

function legalPrivacyLinks(
  legalHref: string,
  privacyHref: string,
  privacyClassName = "privacy",
): FooterLink[] {
  return [
    { label: "Legal", href: legalHref, className: "legal" },
    { label: "Privacy", href: privacyHref, className: privacyClassName },
  ];
}

/**
 * Static per-brand settings for all 17 dashboards. Header/Footer fields come
 * from Vue 2 templates and CoreFooter props. This module does not read
 * `window.__BSS__`.
 */
export const BRAND_CONFIGS: Record<string, BrandConfigRecord> = {
  rc: {
    headerComponent: "rc",
    footerComponent: "rc",
    regionAliases: { americas: "AMERICAS", emea: "EMEA", apac: "APAC" },
    loadBanners: true,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "1999",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "rc-main-logo",
    logoHref: "https://support.ringcentral.com",
    footerLinks: [
      { label: "MyRingCentral", href: "https://support.ringcentral.com/", className: "support" },
      { label: "Legal", href: "https://www.ringcentral.com/legal.html", className: "legal" },
      { label: "Privacy Notice", href: "https://www.ringcentral.com/legal/privacy-notice.html", className: "privacy" },
      { label: "Contact Us", href: "https://support.ringcentral.com/contact-support.html", className: "contact" },
    ],
  },
  att: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: { layout: "att" },
    // The mainLogoUrl/partnersLogoUrl mismatch with the server payload also exists
    // in the original AT&T code. Add a test using the actual server payload and
    // discuss key alignment separately; do not automatically change behavior
    // during migration.
    bssKeys: {
      brandName: { key: "brandName", fallback: "AT&T Office@Hand" },
      mainLogoUrl: { key: "mainLogoUrl", fallback: "" },
      supportLink: { key: "supportLink", fallback: "https://officeathand.att.com/" },
      copyright: { key: "copyright", fallback: "AT&T Intellectual Property. All Rights Reserved." },
      // The static URL and label come from the policy entry in footerLinks.
      tosLink: { key: "tosLink" },
      partnersLink: { key: "supportLink", fallback: "https://www.ringcentral.com/" },
      partnersLogoUrl: { key: "partnersLogoUrl", fallback: "" },
    },
    regionAliases: { americas: "AMERICAS", emea: "EMEA" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "",
    copyrightExtraText: "",
    logoId: "att-main-logo",
    logoElement: "a",
    logoHref: "https://officeathand.att.com/",
    footerLinks: [
      { label: "Terms Of Service", href: "https://www.att.com/officeathandpolicy", className: "policy" },
    ],
  },
  atos: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyrightId: "copyright",
      copyrightSuffix: " Unify All rights reserved",
      noticeLines: [RC_TRADEMARK_NOTICE],
      trailingBreak: true,
    },
    regionAliases: { americas: "AMERICAS", emea: "EMEA", apac: "APAC" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "",
    copyrightExtraText: "",
    logoId: "atos-main-logo",
    logoElement: "a",
    logoHref: "",
    footerLinks: [],
  },
  avaya: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyrightId: "copyright",
      copyrightSuffix: " Avaya",
      noticeLines: ["Avaya and Avaya logo are trademarks of Avaya Intellectual Property.", RC_MARKS_NOTICE],
      trailingBreak: true,
    },
    regionAliases: { americas: "AMERICAS", emea: "EMEA", apac: "APAC" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "",
    copyrightExtraText: "",
    logoId: "avaya-main-logo",
    logoElement: "a",
    logoHref: "",
    footerLinks: [],
  },
  bt: {
    headerComponent: "shared",
    footerComponent: "bt",
    bssKeys: {
      brandName: { key: "brandName", fallback: "BT Cloud Work" },
      serviceSiteMainLogo: { key: "serviceSiteMainLogo", fallback: "" },
      feedbackPage: { key: "feedbackPage", fallback: "https://help.business.bt.com/" },
      privacyPolicyPage: { key: "privacyPolicyPage", fallback: "https://business.bt.com/privacy-policy/" },
      acceptableUsePolicyPage: { key: "acceptableUsePolicyPage", fallback: "https://business.bt.com/terms/" },
      copyright: { key: "copyright", fallback: "RingCentral, Inc. All rights reserved." },
      notificationTradeMark: {
        key: "notificationTradeMark",
        fallback: "RingCentral and the RingCentral logo are trademarks of RingCentral, Inc.",
      },
      logoPowered: { key: "logo_powered", fallback: "" },
    },
    regionAliases: { emea: "United Kingdom" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "",
    copyrightExtraText: "",
    logoId: "bt-main-logo",
    logoElement: "a",
    logoHref: "https://help.business.bt.com/",
    footerLinks: [
      { label: "Contact BT", href: "https://www.bt.com/help/contact-bt/", className: "contact" },
      { label: "Privacy Policy", href: "https://business.bt.com/privacy-policy/", className: "policy" },
      { label: "Terms of use", href: "https://business.bt.com/products/terms-of-use/", className: "terms" },
      {
        label: "Codes of Practice",
        href: "https://www.bt.com/about/bt/policy-and-regulation/our-governance-and-strategy/codes-of-practice",
        className: "codes",
      },
      { label: "Complaints", href: "https://help.business.bt.com/app/complaints", className: "complaints" },
      { label: "Terms and Conditions", href: "https://business.bt.com/terms/", className: "conditions" },
      { label: "Business Help and Support", href: "https://help.business.bt.com/", className: "support" },
    ],
  },
  "charter-ent": {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "footer-menu",
      noticeLines: ["Spectrum Business logo is a registered trademark of Charter Communications, used under license."],
    },
    regionAliases: { americas: "AMERICAS" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2022",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "charter-ent-main-logo",
    logoElement: "div",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "https://enterprise.spectrum.com/legal/terms-and-conditions.html",
      "https://www.ringcentral.com/legal/privacy-policy.html",
      "policy",
    ),
  },
  "charter-smb": {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "footer-menu",
      noticeLines: ["Spectrum Business logo is a registered trademark of Charter Communications, used under license."],
    },
    regionAliases: { americas: "AMERICAS" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2022",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "charter-smb-main-logo",
    logoElement: "div",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "https://www.spectrum.com/policies/terms-of-service",
      "https://www.ringcentral.com/legal/privacy-policy.html",
    ),
  },
  "dt-atos": {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "footer-menu",
      copyrightId: "copyright",
      copyrightSuffix: " Unify All rights reserved",
      noticeLines: [RC_TRADEMARK_NOTICE],
      trailingBreak: true,
    },
    regionAliases: { emea: "Germany" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2021",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "dt-atos-main-logo",
    logoElement: "a",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "https://www.telekom.de/impressum",
      "https://www.telekom.de/ueber-das-unternehmen/datenschutz?wt_mc=alias_datenschutz-ganz-einfach",
    ),
  },
  ecotel: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "flex row footer-menu",
      noticeLines: ["Ecotel is a registered trademark of ecotel Communications, AG.", RC_TRADEMARK_NOTICE],
    },
    regionAliases: { emea: "Germany" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2021",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "ecotel-main-logo",
    logoElement: "div",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "https://www.ecotel.de/footer-navigation/rechtliche-hinweise/impressum",
      "https://www.ecotel.de/footer-navigation/rechtliche-hinweise/datenschutz",
    ),
  },
  frontier: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "flex row footer-menu",
      noticeLines: [
        "Frontier is a registered trademark of Citizens Telecom Services Company L.L.C. d/b/a Frontier Communications.",
        RC_TRADEMARK_NOTICE,
      ],
    },
    regionAliases: { americas: "AMERICAS" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2021",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "frontier-main-logo",
    logoElement: "div",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "https://frontier.com/corporate/terms",
      "https://frontier.com/corporate/terms",
    ),
  },
  mcm: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "flex row footer-menu",
      noticeLines: [
        "Symphony is a registered trademark of Megacable Comunicaciones de México, S.A. de C.V.",
        RC_TRADEMARK_NOTICE,
      ],
    },
    regionAliases: { mexico: "MEXICO" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2021",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "mcm-main-logo",
    logoElement: "div",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "https://www.mcmtelecom.com/aviso-legal",
      "https://www.mcmtelecom.com/politicas-de-privacidad",
    ),
  },
  rise: {
    headerComponent: "rise",
    footerComponent: "shared",
    footer: { layout: "copyright" },
    regionAliases: { americas: "AMERICAS", emea: "EMEA", apac: "APAC" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "1999",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "",
    logoHref: "",
    footerLinks: [],
  },
  telekom: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "footer-menu",
      noticeLines: ["Telekom is a registered trademark of Telekom Deutschland GmbH.", RC_TRADEMARK_NOTICE],
    },
    regionAliases: { emea: "Germany" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2021",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "dt-telekom-main-logo",
    logoElement: "div",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "https://www.telekom.de/impressum",
      "https://www.telekom.de/ueber-das-unternehmen/datenschutz?wt_mc=alias_datenschutz-ganz-einfach",
    ),
  },
  telus: {
    headerComponent: "telus",
    footerComponent: "telus",
    regionAliases: { americas: "Canada" },
    loadBanners: false,
    locales: {
      en_US: "English",
      fr_CA: "Francais",
    },
    copyrightYearFrom: "",
    copyrightExtraText: "",
    logoId: "telus-main-logo",
    logoHref: "",
    footerLinks: [
      {
        label: "Legal",
        href: "https://www.telus.com/en/business/support/article/business-connect-terms",
        className: "legal",
      },
      {
        label: "Service terms",
        href: "https://www.telus.com/en/business/support/article/business-connect-terms",
        className: "terms",
      },
    ],
  },
  verizon: {
    headerComponent: "verizon",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "flex row footer-links",
      noticeLines: ["Verizon is a registered trademark of Verizon Communications, Inc.", RC_MARKS_NOTICE],
      showRingCentralLogo: true,
    },
    regionAliases: { americas: "AMERICAS", emea: "EMEA" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2021",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "rc-main-logo",
    logoHref: "https://www.ringcentral.com/",
    footerLinks: legalPrivacyLinks(
      "https://www.ringcentral.com/legal.html",
      "https://www.ringcentral.com/legal/privacy-notice.html",
    ),
  },
  versatel: {
    headerComponent: "shared",
    footerComponent: "shared",
    footer: {
      layout: "standard",
      copyClass: "footer-copy",
      menuClass: "footer-menu",
      noticeLines: ["1&1 Connected Calls is a registered trademark of 1&1 Versatel Deutschland GmbH.", RC_TRADEMARK_NOTICE],
    },
    regionAliases: { emea: "Germany" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "2021",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "versatel-main-logo",
    logoElement: "div",
    logoHref: "",
    footerLinks: legalPrivacyLinks(
      "http://fs.1und1.net/docs/produkt/allgemein/ex_doku/1624-gesetzliche_kundeninformation.pdf",
      "https://www.1und1.net/datenschutz",
    ),
  },
  vodafone: {
    headerComponent: "vodafone",
    footerComponent: "vodafone",
    regionAliases: { emea: "EMEA", apac: "APAC" },
    loadBanners: false,
    locales: { ...DEFAULT_BRAND_LOCALES },
    copyrightYearFrom: "1999",
    copyrightExtraText: RC_COPYRIGHT_EXTRA,
    logoId: "vodafone-main-logo",
    logoHref: "https://support.vodafonebusiness.ringcentral.com",
    footerLinks: [
      { label: "Support", href: "https://support.vodafonebusiness.ringcentral.com/gb/en/", className: "support" },
      { label: "Legal", href: "https://www.ringcentral.com/legal.html", className: "legal" },
      {
        label: "Privacy Notice",
        href: "https://support.vodafonebusiness.ringcentral.com/privacy-notice.html",
        className: "privacy",
      },
      {
        label: "Sitemap",
        href: "https://support.vodafonebusiness.ringcentral.com/gb/en/sitemap.xml",
        className: "sitemap",
      },
    ],
  },
};

/**
 * Looks up a brand record without Vue or BSS globals. Unknown and empty
 * brand names receive the complete RC configuration, including its brand key.
 */
export function resolveBrandConfig(brand: string): BrandConfig {
  const normalizedBrand = brand.trim().toLowerCase();
  const resolvedBrand = Object.prototype.hasOwnProperty.call(BRAND_CONFIGS, normalizedBrand)
    ? normalizedBrand
    : "rc";

  return {
    brand: resolvedBrand,
    ...BRAND_CONFIGS[resolvedBrand],
  };
}
